import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/store';
import { useAutomationsApi } from '../api';
import { AutomationsState, resetError, setError, setLoading } from '../slices';
import { AutomationSaveRequestData } from '../types';

export const useAutomationService = () => {
  const dispatch = useDispatch();
  const { loading, error } = useAppSelector(state => state.automations);

  const api = useAutomationsApi();

  const withLoading = useCallback(
    async (key: keyof AutomationsState['loading'], fn: () => Promise<unknown>) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ [key]: true }));

        await fn();
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ [key]: false }));
      }
    },
    [dispatch],
  );

  const createAutomation = useCallback(
    (companyId: string, data: AutomationSaveRequestData) =>
      withLoading('create', () => api.createAutomation({ companyId }, data)),
    [withLoading, api],
  );

  const updateAutomation = useCallback(
    (companyId: string, automationId: string, data: AutomationSaveRequestData) =>
      withLoading('update', () => api.updateAutomation(automationId, { companyId }, data)),
    [withLoading, api],
  );

  const deleteAutomation = useCallback(
    (companyId: string, automationId: string) =>
      withLoading('delete', () => api.deleteAutomation(automationId, { companyId })),
    [withLoading, api],
  );

  return {
    loading,
    error,
    createAutomation,
    updateAutomation,
    deleteAutomation,
  };
};
